import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Grid, Card, CardContent, CircularProgress,
  Alert, Button, Table, TableBody, TableCell, TableHead, TableRow, Chip
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { conservationService } from '../services/conservationService';

function Conservation() {
  const [latest, setLatest] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [latestData, historyData] = await Promise.all([
        conservationService.getLatest().catch(() => null),
        conservationService.getHistory()
      ]);
      setLatest(latestData);
      setHistory(historyData.checks || []);
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  const runCheck = async () => {
    try {
      setRunning(true);
      setError(null);
      const result = await conservationService.runCheck();
      setLatest(result);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message);
    } finally {
      setRunning(false);
    }
  };

  if (loading) {
    return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Conservation Check</Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        System-wide mass balance validation (E-ledger Principle 10)
      </Typography>

      <Box sx={{ mb: 3 }}>
        <Button variant="contained" onClick={runCheck} disabled={running}>
          {running ? 'Running...' : 'Run Conservation Check'}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {latest && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12}>
            <Alert severity={latest.conservation_holds ? 'success' : 'error'} icon={latest.conservation_holds ? <CheckCircleIcon /> : <ErrorIcon />}>
              {latest.conservation_holds
                ? 'Conservation principle holds: System-wide mass balance is valid'
                : 'Conservation violation detected: System-wide mass balance discrepancy'}
            </Alert>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card><CardContent>
              <Typography variant="subtitle2" color="text.secondary">Total Debits</Typography>
              <Typography variant="h5">{parseFloat(latest.total_system_debits_kg_co2e).toFixed(2)} kg</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card><CardContent>
              <Typography variant="subtitle2" color="text.secondary">Total Credits</Typography>
              <Typography variant="h5">{parseFloat(latest.total_system_credits_kg_co2e).toFixed(2)} kg</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card><CardContent>
              <Typography variant="subtitle2" color="text.secondary">System Balance</Typography>
              <Typography variant="h5">{parseFloat(latest.total_system_balance_kg_co2e).toFixed(2)} kg</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card><CardContent>
              <Typography variant="subtitle2" color="text.secondary">External Transfers</Typography>
              <Typography variant="h5">{parseFloat(latest.total_external_transfers_kg_co2e).toFixed(2)} kg</Typography>
            </CardContent></Card>
          </Grid>
        </Grid>
      )}

      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>Check History</Typography>
        {history.length === 0 ? (
          <Alert severity="info">No conservation checks have been run yet</Alert>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Timestamp</TableCell>
                <TableCell>Holds</TableCell>
                <TableCell align="right">Debits (kg)</TableCell>
                <TableCell align="right">Credits (kg)</TableCell>
                <TableCell align="right">Balance (kg)</TableCell>
                <TableCell>Ledgers</TableCell>
                <TableCell>Triggered By</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {history.map((check) => (
                <TableRow key={check.check_id}>
                  <TableCell sx={{ fontSize: '0.75rem' }}>{new Date(check.check_timestamp).toLocaleString()}</TableCell>
                  <TableCell>
                    <Chip label={check.conservation_holds ? 'PASS' : 'FAIL'} size="small"
                      color={check.conservation_holds ? 'success' : 'error'} />
                  </TableCell>
                  <TableCell align="right">{parseFloat(check.total_system_debits_kg_co2e).toFixed(2)}</TableCell>
                  <TableCell align="right">{parseFloat(check.total_system_credits_kg_co2e).toFixed(2)}</TableCell>
                  <TableCell align="right">{parseFloat(check.total_system_balance_kg_co2e).toFixed(2)}</TableCell>
                  <TableCell>{check.ledger_count}</TableCell>
                  <TableCell>{check.triggered_by || '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>
    </Box>
  );
}

export default Conservation;
